// src/pages/CombosPage.jsx
// ------------------------------------------------------------------
// Gift Boxes & Combos — Diwali 2026.
// Four price lists (tabs): Branded Gift Boxes (10 pcs), Branded Gift
// Boxes (5 pcs), Angel Branded Gift Boxes (5 pcs) and Combo Packs.
// Adds to the real cart (useCartStore), opens the cart drawer
// (useUIStore), or orders on WhatsApp.
// The poster on the right is drawn in code, so it always shows the
// current shop name from src/config/tenant.js.
//
// To change a price or add a box, edit only the GROUPS list below.
// ------------------------------------------------------------------
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useCartStore, useUIStore } from '../store';
import { ADMIN_WHATSAPP_NUMBER, SHOP_NAME, SHOP_PHONES } from '../config/tenant';
import FireworksCanvas from '../components/fx/FireworksCanvas';
import '../components/fx/fx.css';
import './CombosPage.css';

const BRAND = SHOP_NAME;
const money = (n) => '₹' + n.toLocaleString('en-IN');
const waLink = (text) => `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const PALETTE = ['#06d6a0', '#3a86ff', '#8338ec', '#ff006e', '#fb5607', '#e63946', '#118ab2', '#f15bb5', '#2a9d8f', '#d62828'];

/* ---------------- PRICE LISTS ---------------- */
const RAW_GROUPS = [
  {
    key: 'gb10', tab: 'Gift Box · 10 Pcs', title: 'Branded Gift Boxes', sub: '10 PCS', emoji: '🪔', kind: 'box',
    rows: [
      [20, 'Vinayagar', 450], [25, 'Murugan', 480], [30, 'Krishnan', 580],
      [35, 'Shivan Parvathy', 700], [42, 'Lakshmi', 900], [50, 'Balaji', 1150],
    ],
  },
  {
    key: 'gb5', tab: 'Gift Box · 5 Pcs', title: 'Branded Gift Boxes', sub: '5 PCS', emoji: '🎁', kind: 'box',
    rows: [
      [19, 'Dora', 390], [21, 'Mona', 410], [26, 'Avenger', 460], [30, 'Avatar', 550],
      [36, 'Hulk', 660], [40, 'Angry Birds', 700], [45, 'Paradise', 930],
      [50, 'Wonderful', 1050], [55, 'Carnival', 1200], [60, 'VIP', 1350],
    ],
  },
  {
    key: 'angel', tab: 'Angel Gift Box', title: 'Angel Branded Gift Boxes', sub: '5 PCS', emoji: '😇', kind: 'box',
    rows: [
      [20, 'Battler Box', 450], [25, 'Prince', 500], [30, 'Meta', 600],
      [35, 'Gemini', 680], [40, 'AI', 750], [50, 'VIP', 1100],
    ],
  },
  {
    key: 'combo', tab: 'Combo Packs', title: 'Combo Packs', sub: 'BOX', emoji: '🎆', kind: 'combo',
    rows: [
      [null, 'Arjuna Pack', 3000], [null, 'Kumki Pack', 4000],
      [null, 'Komban Pack', 5000], [null, 'Padayappa Pack', 6000],
    ],
  },
];

const GROUPS = RAW_GROUPS.map((g) => {
  const maxPrice = Math.max(...g.rows.map((r) => r[2]));
  return {
    ...g,
    items: g.rows.map(([items, name, price], i) => ({
      id: `${g.key}-${slug(name)}`,
      group: g.key, kind: g.kind, items, name, price,
      emoji: g.emoji, color: PALETTE[i % PALETTE.length],
      top: price === maxPrice,
      fullName: g.kind === 'combo'
        ? `${name} (Combo)`
        : `${items} Items (${name.toUpperCase()}) — ${g.title} (${g.sub})`,
    })),
  };
});
const findItem = (id) => GROUPS.flatMap((g) => g.items).find((x) => x.id === id);

/* Picture (SVG data URI) used as the cart thumbnail */
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
const giftImage = (g) => {
  const big = g.kind === 'combo' ? '🎆' : String(g.items);
  const small = g.kind === 'combo' ? 'COMBO' : 'ITEMS';
  const title = esc(g.name.toUpperCase());
  const fs = title.length > 12 ? 14 : 20;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
  <defs><linearGradient id='b' x1='0' x2='1'><stop offset='0' stop-color='#8f1016'/><stop offset='.5' stop-color='#e63946'/><stop offset='1' stop-color='#8f1016'/></linearGradient>
  <linearGradient id='g' x1='0' x2='1'><stop offset='0' stop-color='#b8860b'/><stop offset='.5' stop-color='#ffe066'/><stop offset='1' stop-color='#b8860b'/></linearGradient></defs>
  <rect width='200' height='200' fill='#2a0f3a'/>
  <rect x='38' y='82' width='124' height='92' rx='6' fill='url(#b)'/>
  <rect x='30' y='64' width='140' height='28' rx='6' fill='#c1121f'/>
  <rect x='90' y='64' width='20' height='110' fill='url(#g)'/>
  <path d='M100 64 C70 30 50 50 70 64 Z M100 64 C130 30 150 50 130 64 Z' fill='url(#g)'/>
  <text x='100' y='142' text-anchor='middle' font-family='Arial Black,Arial' font-weight='900' font-size='30' fill='#fff' stroke='#5c0b1f' stroke-width='1.5'>${big}</text>
  <text x='100' y='162' text-anchor='middle' font-family='Arial' font-weight='700' font-size='13' fill='#ffe066'>${small}</text>
  <text x='100' y='36' text-anchor='middle' font-family='Arial Black,Arial' font-weight='900' font-size='${fs}' fill='#ffd60a'>${title}</text>
</svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
};

const giftToProduct = (g) => ({
  id: g.id,
  name: g.fullName,
  selling_price: g.price, offer_price: null, has_offer: false,
  stock_qty: 999, tax_rate: 0,
  image_url: giftImage(g), thumb_url: giftImage(g),
  is_combo: true, combo_items: g.items,
});

/* ---------------- Poster (drawn in code) ---------------- */
function GiftPoster({ group, picked, onPick }) {
  const isCombo = group.kind === 'combo';
  return (
    <figure className="gp" aria-label={`${BRAND} Diwali 2026 ${group.title} price list`}>
      <FireworksCanvas density={1.4} />
      <div className="gp-inner">
        <p className="gp-brand">{BRAND}</p>
        <p className="gp-year">🪔 Diwali 2026 🪔</p>
        <p className="gp-title gp-title-sm">{group.title}</p>
        {!isCombo && <p className="gp-sub">{group.sub}</p>}

        <div className={`gp-table ${isCombo ? 'gp-2col' : ''}`}>
          <div className="gp-th">
            {isCombo ? <><span>PACK</span><span>RATE</span></> : <><span>ITEMS</span><span>BOX</span><span>RATE</span></>}
          </div>
          {group.items.map((g) => (
            <button key={g.id} type="button" onClick={() => onPick(g.id)}
              className={`gp-tr ${picked === g.id ? 'is-on' : ''}`}>
              {!isCombo && <span className="gp-n">{g.items}</span>}
              <span className="gp-name">{g.name}</span>
              <span className="gp-rate">{money(g.price)}</span>
            </button>
          ))}
        </div>

        <p className="gp-phone">📞 {SHOP_PHONES[0]}</p>
      </div>
    </figure>
  );
}

export default function CombosPage() {
  const addItem  = useCartStore((s) => s.addItem);
  const openCart = useUIStore((s) => s.openCart);
  const [groupKey, setGroupKey] = useState(GROUPS[0].key);
  const group = GROUPS.find((g) => g.key === groupKey);
  const [pickedId, setPickedId] = useState(GROUPS[0].items[2].id);
  const picked = findItem(pickedId);
  const gift = picked && picked.group === groupKey ? picked : group.items[0];

  const switchGroup = (key) => {
    setGroupKey(key);
    const g = GROUPS.find((x) => x.key === key);
    setPickedId(g.items[Math.min(2, g.items.length - 1)].id);
  };

  const label = (g) => (g.kind === 'combo' ? g.name : `${g.items} Items · ${g.name}`);

  const add = (g) => {
    const res = addItem(giftToProduct(g), 1);
    if (res && res.error) { toast.error(res.error); return; }
    toast.success(`${g.kind === 'combo' ? g.name : `Gift Box ${g.name}`} added to cart`);
    openCart();
  };

  const orderWA = (g) => {
    const msg = `Hello ${BRAND},\nI would like to order:\n\n${g.fullName} — ${money(g.price)}\nQuantity: 1\n\nPlease share delivery and payment details.`;
    window.open(waLink(msg), '_blank', 'noopener');
  };

  return (
    <div className="mp-combos page-top">
      <section className="gb-stage" aria-labelledby="gb-title">
        <div className="mp-wrap gb-inner">
          <div className="gb-copy">
            <span className="gb-ribbon">🪔 Diwali 2026 special</span>
            <h1 id="gb-title" className="gb-title">Gift Box &amp; Combos</h1>
            <p className="gb-lead">Ready-packed assorted crackers in festive branded gift boxes, plus big combo packs for the whole family. Pick a list, then pick a box.</p>

            <div className="gb-tabs" role="tablist" aria-label="Price lists">
              {GROUPS.map((g) => (
                <button key={g.key} role="tab" type="button" aria-selected={groupKey === g.key}
                  className={`gb-tab ${groupKey === g.key ? 'is-on' : ''}`} onClick={() => switchGroup(g.key)}>
                  {g.emoji} {g.tab}
                </button>
              ))}
            </div>

            <div className="gb-board" role="radiogroup" aria-label={`Choose from ${group.title}`}>
              <div className="gb-row gb-headrow" aria-hidden="true">
                <span>{group.kind === 'combo' ? 'Pack' : 'Item'}</span><span>Rate</span>
              </div>
              {group.items.map((g) => (
                <button
                  key={g.id}
                  role="radio"
                  aria-checked={gift.id === g.id}
                  className={`gb-row ${gift.id === g.id ? 'gb-on' : ''} ${g.top ? 'gb-vip' : ''}`}
                  onClick={() => setPickedId(g.id)}
                >
                  <span className="gb-item">
                    {g.kind === 'combo'
                      ? <>{g.name}</>
                      : <>{g.items} Items<em className="gb-tier" style={{ background: g.color }}>{g.name}</em></>}
                  </span>
                  <span className="gb-rate">{money(g.price)}</span>
                </button>
              ))}
            </div>

            <div className="gb-buy">
              <div className="gb-sel">
                <span className="gb-sel-l">Selected</span>
                <span className="gb-sel-v">{label(gift)} · <b>{money(gift.price)}</b></span>
              </div>
              <div className="gb-btns">
                <button className="mp-act mp-a-cart" onClick={() => add(gift)}>🛒 Add to cart</button>
                <button className="mp-act mp-a-wa" onClick={() => orderWA(gift)}>💬 Order on WhatsApp</button>
              </div>
            </div>
          </div>

          <GiftPoster group={group} picked={gift.id} onPick={setPickedId} />
        </div>
      </section>
    </div>
  );
}
